import styles from './Button.module.css';
import { classList } from '../../helpers/classList';
import { Spinner } from '../Spinner';
import { MouseEventHandler, ReactNode } from 'react';

/**
 * What the button looks like.
 *
 * `outline` is the default and the everyday action; `cta` is the single leaf
 * primary action on a page; `quiet` is Cancel/Back/row actions; `danger` is
 * destructive confirmation.
 *
 * `primary` / `secondary` / `tertiary` are the original names and still work.
 * They read backwards — `primary` is the quiet outline and `secondary` is the
 * loud CTA — which is why 69 of 70 call sites, all of which pass no level at
 * all, render the outline. Prefer the names above in new code.
 */
export type ButtonLevel =
  | 'cta'
  | 'outline'
  | 'quiet'
  | 'danger'
  /** @deprecated use `outline` */
  | 'primary'
  /** @deprecated use `cta` */
  | 'secondary'
  /** @deprecated use `quiet` */
  | 'tertiary';

const LEVEL_CLASS: Record<ButtonLevel, keyof typeof styles> = {
  cta: 'cta',
  outline: 'outline',
  quiet: 'quiet',
  danger: 'danger',
  primary: 'outline',
  secondary: 'cta',
  tertiary: 'quiet',
};

export interface ButtonProps {
  className?: string;
  type?: 'submit' | 'reset' | 'button';
  level?: ButtonLevel;
  /** `large` is the reference submit pill: 16px 48px padding, 280px min-width. */
  size?: 'small' | 'medium' | 'large';
  /** Leading 20px icon (an inline SVG), as on the reference Upload button. */
  icon?: ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
  submitting?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children?: React.ReactNode;
}

export const Button = ({
  className,
  fullWidth,
  disabled,
  submitting,
  type,
  // Unchanged: `primary` maps to the outline class, so existing call sites
  // render exactly as they did before.
  level = 'primary',
  size = 'medium',
  icon,
  onClick,
  children,
}: ButtonProps) => {
  return (
    <button
      type={type}
      className={classList(
        styles.btn,
        styles[LEVEL_CLASS[level]],
        styles[size],
        submitting ? styles.submitting : undefined,
        fullWidth ? styles.fullwidth : undefined,
        className,
      )}
      onClick={onClick}
      disabled={disabled || submitting}
    >
      {icon && (
        <span className={styles.icon} aria-hidden='true'>
          {icon}
        </span>
      )}
      {children}
      {submitting && <Spinner size='small' className={styles.spinner} />}
    </button>
  );
};
